import { useState, useCallback } from 'react';

import { isString } from 'lodash';

import useEventListener from 'hooks/useEventListener';

const useLocalStorage = (key, initialValue) => {
  // Get from local storage then
  // parse stored json or return initialValue
  const readValue = useCallback(() => {
    try {
      const raw = window.localStorage.getItem(key);

      if (!raw) {
        return initialValue;
      }

      const parsedItem = JSON.parse(raw);
      return isString(parsedItem) ? raw.replace(/"/g, '') : parsedItem;
    } catch (error) {
      console.warn(`Error reading localStorage key “${key}”:`, error);
      return initialValue;
    }
  }, [initialValue, key]);

  const [state, setState] = useState(() => readValue());

  const setValue = value => {
    setState(value);
    window.localStorage.setItem(key, JSON.stringify(value));
    // We dispatch a custom event so every similar useLocalStorage hook is notified
    window.dispatchEvent(new StorageEvent('local-storage', { key }));
  };

  const handleStorageChange = useCallback(
    event => {
      if (event?.key !== key) {
        return;
      }
      setState(readValue());
    },
    [key, readValue],
  );

  // this only works for other documents, not the current one
  useEventListener('storage', handleStorageChange);

  // this is a custom event
  useEventListener('local-storage', handleStorageChange);

  return [state, setValue];
};

export default useLocalStorage;
